import * as React from "react";
import { cn } from "../_internal/cn";
import type { TypeNarrativeSectionProps } from "./NarrativeSectionTypes";

/**
 * NarrativeSection — a semantic <section> wrapper that groups related narrative
 * content under a labeled heading (e.g. a "What's Happening" block that holds a
 * summary and a pull quote within a brief).
 *
 * Renders a required sectionTitle as a bold heading (14px / 24px,
 * container-text-headline token) so the section carries an accessible name; the
 * heading level is configurable via headingLevel (defaults to <h2>). Content
 * passed as children stacks below the title.
 *
 * Carries no surface, shadow, or accent of its own — drop it into a
 * NarrativeContainer (see the stories / playground). Styled via
 * narrative-section.css (Seeds CSS custom properties); consumers import the
 * classes through @sproutsocial/racine/css/components. Overrides come via
 * standard className / style.
 */
const NarrativeSection = React.forwardRef<
  HTMLElement,
  TypeNarrativeSectionProps
>(({ sectionTitle, headingLevel = 2, children, className, ...props }, ref) => {
  const Heading = `h${headingLevel}` as const;

  return (
    <section
      ref={ref}
      className={cn("seeds-narrative-section", className)}
      {...props}
    >
      <Heading className="seeds-narrative-section-title">
        {sectionTitle}
      </Heading>
      {children != null && (
        <div className="seeds-narrative-section-content">{children}</div>
      )}
    </section>
  );
});

NarrativeSection.displayName = "NarrativeSection";

export default NarrativeSection;
